import { FC, ReactNode } from 'react';

import {
    BodyText,
    Button,
    ButtonGroup,
    Card,
    Eyebrow,
    Headline,
    Icon,
    IconNameType,
    Layout,
    Page,
    Stack,
} from '@servicetitan/design-system';

import { AIRTABLE_APPLICATION_URL } from '../constants';
import { Steps } from '../step';
import { RequestAccessHeader } from './request-access-header';

interface WhyJoinCardProps {
    iconName: IconNameType;
    title: string;
    description: ReactNode;
}

const WhyJoinCard: FC<WhyJoinCardProps> = ({ iconName, title, description }) => (
    <Card style={{ height: '-webkit-fill-available' }}>
        <Card.Section style={{ height: '-webkit-fill-available' }}>
            <Stack direction="row" spacing={2} alignItems="flex-start">
                <Icon name={iconName} size={24} />
                <Stack direction="column" spacing={1}>
                    <BodyText bold>{title}</BodyText>
                    <BodyText>{description}</BodyText>
                </Stack>
            </Stack>
        </Card.Section>
    </Card>
);
WhyJoinCard.displayName = 'WhyJoinCard';

interface ThirdPartyStepProps {
    onBack: () => void;
}

export const ThirdPartyStep: FC<ThirdPartyStepProps> = ({ onBack }) => {
    return (
        <Page
            header={<RequestAccessHeader currentStep={Steps.GetAccess} />}
            footer={
                <Stack className="w-100" justifyContent="flex-end">
                    <ButtonGroup>
                        <Button onClick={onBack}>Back</Button>
                        <Button primary href={AIRTABLE_APPLICATION_URL}>
                            Apply Now
                        </Button>
                    </ButtonGroup>
                </Stack>
            }
        >
            <Stack direction="column" spacing={3}>
                <Stack direction="column" spacing={1}>
                    <Headline el="h2" size="medium">
                        ServiceTitan App Marketplace
                    </Headline>
                    <BodyText>
                        Join our platform and offer your integration to thousands of home and
                        commercial service businesses.
                    </BodyText>
                </Stack>

                <Stack direction="column">
                    <Eyebrow className="m-b-1">Why Join</Eyebrow>
                    <Layout type="2Col" spacing="compact" className="align-items-stretch-i">
                        <Layout.Section>
                            <WhyJoinCard
                                iconName="people"
                                title="Access to ServiceTitan’s Customer Base"
                                description="Showcase your app to the entire ServiceTitan customer base through a centralized marketplace, increasing visibility and revenue opportunities."
                            />
                        </Layout.Section>
                        <Layout.Section>
                            <WhyJoinCard
                                iconName="security"
                                title="Built-in Customer Trust"
                                description="When your app appears in the ServiceTitan Marketplace, customers trust it more, reducing the barrier to adoption."
                            />
                        </Layout.Section>
                        <Layout.Section>
                            <WhyJoinCard
                                iconName="check_circle"
                                title="Seamless Integration"
                                description="Apps built on ServiceTitan’s APIs integrate smoothly into existing customer workflows, removing complex setup and reducing costly development for you and your customers."
                            />
                        </Layout.Section>
                        <Layout.Section>
                            <WhyJoinCard
                                iconName="trending_up"
                                title="Technical Support"
                                description="Access comprehensive API documentation and developer resources to bring your integration to market faster."
                            />
                        </Layout.Section>
                    </Layout>
                </Stack>

                <Stack direction="column" spacing={1}>
                    <Eyebrow>To Get Started</Eyebrow>
                    <ol className="m-y-0 p-l-3 fw-bold">
                        <li>
                            Apply
                            <BodyText className="fw-normal m-y-1">
                                Submit your company and integration details. We evaluate all
                                submissions based on user base size, product-market fit, technical
                                capability, and compliance with ServiceTitan security standards.
                            </BodyText>
                        </li>
                        <li>
                            Discovery Call
                            <BodyText className="fw-normal m-y-1">
                                If your application is selected, the ServiceTitan team will meet
                                with you to discuss your integration workflow and explain the
                                process.
                            </BodyText>
                        </li>
                        <li>
                            Review &amp; Proposal
                            <BodyText className="fw-normal m-y-1">
                                Our team evaluates your workflow and provides an approved proposal
                                with permitted use cases and API endpoints.
                            </BodyText>
                        </li>
                        <li>
                            Sign &amp; Build
                            <BodyText className="fw-normal m-y-1">
                                Complete the partnership agreement to access your integration
                                environment and start building with our team.
                            </BodyText>
                        </li>
                        <li>
                            Certification &amp; Launch
                            <BodyText className="fw-normal m-y-1">
                                Demo your completed integration for review. Once we verify and
                                approve it, your integration goes live and becomes discoverable to
                                ServiceTitan users.
                            </BodyText>
                        </li>
                    </ol>
                </Stack>
            </Stack>
        </Page>
    );
};
ThirdPartyStep.displayName = 'ThirdPartyStep';
